'use client';
import { useEffect, useState } from 'react';

import { useTranslation } from '@/lib/i18n/i18n.client';
import { cn } from '@/lib/utils';

import { WithRef } from '@/cms/types/types';
import { returnRefAndGenerateDataTracker } from '@/decodo/components/atoms/Button/utils';

import Button, { ButtonProps } from '@/decodo/components/atoms/Button';
import { InputFieldProps } from '@/decodo/components/atoms/Input';
import { InputLabel } from '@/decodo/components/atoms/Input/InputLabel';
import Popover, { PopoverProps } from '@/decodo/components/atoms/Popover';

import ImageStrapi, { ImageStrapiProps } from '../Image/ImageStrapi';

import IconTrashCan from '~/icons/trash-01.svg';
import IconChevronDown from '~/svg/IconChevronDown.svg';

export type DropdownOption = {
  label: string;
  value: string;
  icon: ImageStrapiProps;
};

export type InputDropdownWithImageProps<T extends DropdownOption> = {
  options: T[];
  onOptionChange: (value: any) => void;
  value?: string;
  hasSearch?: boolean;
  isDisabled?: boolean;
  classNames?: {
    root?: string;
    helpText?: string;
    inputField?: ButtonProps['classNames'];
    dropdown?: string;
  };
  renderOption?: ({ option }: { option: T }) => React.ReactNode;
  renderSelectedValue?: ({ option }: { option: T }) => React.ReactNode;
  popoverProps?: Partial<PopoverProps>;
} & Omit<InputFieldProps, 'classNames'> & {
    targetClassNames?: InputFieldProps['classNames'];
  };

export const InputDropdownWithImage = <T extends DropdownOption>({
  onOptionChange,
  options: propOptions,
  name,
  isDisabled,
  value,
  ref,
  classNames,
  renderOption,
  renderSelectedValue,
  ...props
}: WithRef<HTMLInputElement, InputDropdownWithImageProps<T>>) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [options, setOptions] = useState(propOptions);
  const selectedOption = propOptions.find((option) => option.value === value);

  const { texts } = useTranslation();

  function handleSelect(value: T['value']) {
    onOptionChange(value);
    setIsOpen(false);
  }

  useEffect(() => {
    setOptions([{ value: '', label: 'Clear selection' } as T, ...propOptions]);
  }, [propOptions]);

  return (
    <div className={cn('relative w-full', classNames?.root)}>
      <div className='align-center relative flex w-full items-center'>
        <Popover
          {...props.popoverProps}
          classNames={{
            target: cn(
              'w-full group/input-dropdown',
              props.popoverProps?.classNames?.target
            ),
            childrenWrapper: cn(
              'border-gray-300 border mt-2 py-0 px-0 rounded w-full min-w-[140px]',
              props.popoverProps?.classNames?.childrenWrapper
            ),
          }}
          position='bottom'
          showArrow={false}
          onChange={(value) => {
            if (!isDisabled) setIsOpen(value);
          }}
          isOpen={isDisabled ? false : isOpen}
          contentProps={{
            ...props.popoverProps?.contentProps,
            avoidCollisions: false,
          }}
          target={
            <div className='flex w-full flex-col'>
              {props.label && (
                <InputLabel
                  label={props.label}
                  required={props.isRequired}
                  fieldName={name}
                  classNames={{
                    label: props.targetClassNames?.label?.root,
                  }}
                />
              )}
              <Button
                onClick={() => {
                  if (!isDisabled) {
                    setIsOpen(true);
                  }
                }}
                type='button'
                role='presentation'
                tabIndex={0}
                variant='input'
                classNames={{
                  ...classNames?.inputField,
                  root: cn(
                    'w-full h-[54px] border group-data-[state=open]/input-dropdown:border-brand-500 group-data-[state=open]/input-dropdown:border-[2px] text-left',
                    isDisabled
                      ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                      : '',
                    classNames?.inputField?.root
                  ),
                  inner: cn(
                    'h-auto flex w-full overflow-hidden text-ellipsis inline-block',
                    classNames?.inputField?.inner
                  ),
                }}
                leftSection={
                  <>
                    {selectedOption?.icon && (
                      <div className='hidden h-8 w-8 md:block'>
                        <ImageStrapi
                          block={selectedOption.icon}
                          inner={{
                            classNames: { image: 'h-8 w-8 object-contain' },
                          }}
                        />
                      </div>
                    )}
                  </>
                }
                rightSection={
                  <IconChevronDown
                    className={cn(
                      'ml-auto h-6 w-6 transition duration-500 ease-in-out',
                      isOpen && !isDisabled && 'rotate-180 text-gray-950',
                      isDisabled && 'text-gray-400'
                    )}
                  />
                }
              >
                <div className='truncane'>
                  {!value
                    ? props.placeholder || 'No selection'
                    : renderSelectedValue
                    ? renderSelectedValue({
                        option: propOptions.find(
                          (option) => option.value === value
                        )!,
                      })
                    : propOptions.find((option) => option.value === value)
                        ?.label || value}
                </div>
              </Button>
            </div>
          }
        >
          <div>
            <ul
              className={cn(
                'max-h-80 w-full overflow-y-auto text-gray-950 hover:no-underline',
                classNames?.dropdown,
                isOpen ? 'block' : 'hidden'
              )}
            >
              {options.length > 0 ? (
                options.map((option, index) => {
                  const isClear = option.value === '';
                  return (
                    <li
                      key={index}
                      className={cn(
                        'relative mt-3 flex h-[54px] cursor-pointer items-center gap-3 px-4 hover:bg-gray-50',
                        option.value === value && 'text-brand-500'
                      )}
                      ref={(_ref) => {
                        returnRefAndGenerateDataTracker(_ref, ref);
                      }}
                      onClick={() => handleSelect(option.value)}
                      data-tracker-context={option.value}
                    >
                      <div className='flex h-8 w-8 items-center justify-center'>
                        {isClear ? (
                          <IconTrashCan className='h-4 w-4' />
                        ) : (
                          option.icon && (
                            <ImageStrapi
                              block={option.icon}
                              inner={{
                                classNames: { image: 'h-8 w-8 object-contain' },
                              }}
                            />
                          )
                        )}
                      </div>
                      {isClear ? (
                        <span className='text-body-m font-mono'>
                          Clear selection
                        </span>
                      ) : renderOption ? (
                        renderOption({ option })
                      ) : (
                        option.label
                      )}
                      {isClear && (
                        <div className='absolute bottom-0 left-2 right-2 border-b border-gray-200' />
                      )}
                    </li>
                  );
                })
              ) : (
                <li className='p-1 px-4'>{texts.input_dropdown.no_options}</li>
              )}
            </ul>
          </div>
        </Popover>
      </div>
    </div>
  );
};
